import { supabase } from '../config/supabase.js'
import { showSuccess, showError } from '../utils/toast.js'
import { navigateToList } from '../router/router.js'
import './password-setup.css'

export function showPasswordSetup() {
  document.getElementById('content').classList.add('hidden')
  document.getElementById('person-detail-page').classList.add('hidden')
  document.getElementById('person-edit-page').classList.add('hidden')
  document.getElementById('person-add-page').classList.add('hidden')
  document.getElementById('user-profile-page').classList.add('hidden')
  document.getElementById('password-setup-page').classList.remove('hidden')
}

export async function loadPasswordSetup() {
  const container = document.getElementById('password-setup-page')
  
  // Get current user
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    showError('Please log in to set a password')
    navigateToList()
    return
  }

  // Check if user already has a password
  const { data: profile } = await supabase
    .from('user_profile')
    .select('has_password')
    .eq('id', user.id)
    .single()

  const hasPassword = profile?.has_password || false

  container.innerHTML = `
    <div class="password-setup-container">
      <button id="back-from-password-setup" class="back-btn">← Back</button>
      
      <div class="password-setup-card">
        <h2>${hasPassword ? 'Change Your Password' : 'Set Up Password Login'}</h2>
        <p class="subtitle">
          ${hasPassword 
            ? 'Enter your current password and choose a new one.' 
            : 'Create a password for faster future logins (optional).'
          }
        </p>

        ${hasPassword ? `
          <div class="info-box info-box-warning">
            <strong>⚠️ Important:</strong> You'll need to use this new password on your next login.
          </div>
        ` : `
          <div class="info-box info-box-info">
            <strong>💡 Tip:</strong> You can still use magic links even after setting a password.
          </div>
        `}

        <form id="password-setup-form" class="setup-form">
          ${hasPassword ? `
            <div class="form-group">
              <label for="current-password">Current Password</label>
              <input 
                type="password" 
                id="current-password" 
                required
                autocomplete="current-password"
                placeholder="Enter your current password"
              >
            </div>
          ` : ''}

          <div class="form-group">
            <label for="new-password">New Password</label>
            <input 
              type="password" 
              id="new-password" 
              required
              minlength="8"
              autocomplete="new-password"
              placeholder="At least 8 characters"
            >
            <small class="form-hint">Minimum 8 characters</small>
          </div>

          <div class="form-group">
            <label for="confirm-password">Confirm Password</label>
            <input 
              type="password" 
              id="confirm-password" 
              required
              minlength="8"
              autocomplete="new-password"
              placeholder="Re-enter your password"
            >
          </div>

          <div id="password-strength" class="password-strength hidden">
            <div class="strength-label">Password Strength:</div>
            <div class="strength-bar">
              <div id="strength-bar-fill" class="strength-bar-fill"></div>
            </div>
            <div id="strength-text" class="strength-text"></div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn-secondary" id="cancel-password-setup">
              Cancel
            </button>
            <button type="submit" class="btn-primary" id="submit-password-setup">
              ${hasPassword ? 'Update Password' : 'Set Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  `

  setupEventListeners(user.id, hasPassword)
}

function setupEventListeners(userId, hasPassword) {
  const form = document.getElementById('password-setup-form')
  const backBtn = document.getElementById('back-from-password-setup')
  const cancelBtn = document.getElementById('cancel-password-setup')
  const submitBtn = document.getElementById('submit-password-setup')
  const newPasswordInput = document.getElementById('new-password')
  const confirmPasswordInput = document.getElementById('confirm-password')
  const currentPasswordInput = document.getElementById('current-password')

  // Back and Cancel buttons
  backBtn.addEventListener('click', () => navigateToList())
  cancelBtn.addEventListener('click', () => navigateToList())

  // Password strength indicator
  newPasswordInput.addEventListener('input', (e) => {
    updatePasswordStrength(e.target.value)
  })

  // Form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault()

    const newPassword = newPasswordInput.value
    const confirmPassword = confirmPasswordInput.value
    const currentPassword = currentPasswordInput?.value

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match')
      confirmPasswordInput.focus()
      return
    }

    // Validate password strength
    if (newPassword.length < 8) {
      showError('Password must be at least 8 characters')
      newPasswordInput.focus()
      return
    }

    submitBtn.disabled = true
    submitBtn.textContent = 'Setting up...'

    try {
      // Get current user email
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      if (!currentUser) {
        throw new Error('Not logged in')
      }

      if (hasPassword && currentPassword) {
        // Verify current password first by attempting to sign in
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: currentUser.email,
          password: currentPassword
        })

        if (signInError) {
          throw new Error('Current password is incorrect')
        }
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (updateError) {
        console.error('Update password error:', updateError)
        throw updateError
      }

      // Update profile to mark has_password = true
      const { error: profileError } = await supabase
        .from('user_profile')
        .update({ 
          has_password: true,
          prefer_magic_link: false
        })
        .eq('id', userId)

      if (profileError) {
        console.error('Profile update error:', profileError)
        throw profileError
      }

      showSuccess(hasPassword ? 'Password updated successfully!' : 'Password set successfully!')
      
      // Remove any password setup banners
      const banner = document.getElementById('password-setup-banner')
      if (banner) banner.remove()

      // Navigate back after a short delay
      setTimeout(() => navigateToList(), 1500)

    } catch (error) {
      console.error('Password setup error:', error)
      showError(error.message || 'Failed to set password')
      submitBtn.disabled = false
      submitBtn.textContent = hasPassword ? 'Update Password' : 'Set Password'
    }
  })
}

function updatePasswordStrength(password) {
  const strengthContainer = document.getElementById('password-strength')
  const strengthBarFill = document.getElementById('strength-bar-fill')
  const strengthText = document.getElementById('strength-text')

  if (!password) {
    strengthContainer.classList.add('hidden')
    return
  }

  strengthContainer.classList.remove('hidden')

  let strength = 0
  let feedback = []

  // Length check
  if (password.length >= 8) strength++
  if (password.length >= 12) strength++

  // Character variety
  if (/[a-z]/.test(password)) strength++
  if (/[A-Z]/.test(password)) strength++
  if (/[0-9]/.test(password)) strength++
  if (/[^a-zA-Z0-9]/.test(password)) strength++

  // Determine strength level
  let level, color, text
  if (strength <= 2) {
    level = 'weak'
    color = '#ef4444'
    text = 'Weak'
    feedback.push('Use at least 8 characters')
    feedback.push('Mix uppercase and lowercase')
  } else if (strength <= 4) {
    level = 'medium'
    color = '#f59e0b'
    text = 'Medium'
    feedback.push('Consider adding numbers or symbols')
  } else {
    level = 'strong'
    color = '#10b981'
    text = 'Strong'
  }

  // Update UI
  const percentage = (strength / 6) * 100
  strengthBarFill.style.width = `${percentage}%`
  strengthBarFill.style.backgroundColor = color
  strengthText.textContent = text
  strengthText.style.color = color
}
